import { listRooms } from '#~/api/agent/index.js';
import { toHexFromId } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
// ENG-4154: identity comes from the IdentityState store now; this hook is a
// thin selector over it (W1 §1b). Per rule: reference-rules-in-comments.
// PHASE D: also self-start orchestration so the resolver runs on ANY route.
import {
  ensureIdentityOrchestration,
  useLinkedAgentIDSelector
} from '#~/electron/identity-store.js';
import * as persistentLayer from '#~/persistence/index.js';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type { Room as M_Room } from '@studio/models/room';
import { useQuery } from '@tanstack/react-query';
import type { AgentRegistryGetRoomResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useEffect, useRef, useState } from 'react';

/**
 * Hook to obtain the currently-linked agent id for this Electron window.
 *
 * ENG-4154: this is now a THIN SELECTOR over the renderer `IdentityState`
 * store (`#~/electron/identity-store.ts`), which is the single source of
 * truth fed by a main→renderer push + a centralized bounded pull fallback.
 * The old per-mount 60×500ms poll loop (and its `getBridgeLinkedEncoderID`
 * pRetry fan-out) is gone — every consumer reads ONE store instead of each
 * running its own poll. `loaded` maps to the store's `settled` flag —
 * `linked`/`error`/prime settle immediately; an early `unlinked` only settles
 * at the session ceiling (the old hook's ≤30s patience, owned by the store) —
 * preserving the `[agentId, loaded]` signature every caller already expects.
 *
 * Per rule: stay-in-scope (ai.knowledge/global) — signature unchanged, no
 * caller edits required. Per rule: reference-rules-in-comments — the store
 * owns the readiness semantics and the session-keyed ceiling.
 *
 * @returns [agentId, loaded]
 */
export const useLinkedAgentID = (): [T_Maybe<Agent['id']>, boolean] => {
  // ENG-4154 PHASE D regression fix: the reactive identity read must self-start
  // the resolver on EVERY boot-protected route. `initIdentity` only fires from
  // `useRootBootstrap` (index route), so a refresh landing on `/room/:id/boot`
  // left the store stuck `resolving` and the BootOverlay never cleared. Pre-
  // migration this hook ran its own poll (self-starting on any route); kicking
  // orchestration idempotently here restores that property. Per rule:
  // reference-rules-in-comments — see knowledge electron-auth-bootstrap-race.
  useEffect(() => {
    ensureIdentityOrchestration();
  }, []);

  return useLinkedAgentIDSelector();
};

export const useLinkedRoom = () => {
  const [linkedAgentID, loaded] = useLinkedAgentID();
  const loadedAgentId = useRef(linkedAgentID);
  loadedAgentId.current = linkedAgentID ? linkedAgentID : loadedAgentId.current;

  const { getAccessTokenSilently } = useAuth0();

  const {
    data: roomId,
    isLoading,
    isPending
  } = useQuery({
    enabled: loaded,
    queryKey: ['linkedRoom', loadedAgentId.current],
    queryFn: async () => {
      let rooms: AgentRegistryGetRoomResponse[] = [];

      try {
        const token: string = await getAccessTokenSilently();
        rooms = await listRooms(token);
      } catch (error_) {
        console.error('Unable to load linked agent.', error_);
      }

      const exists = rooms.find(
        (room) =>
          room.agents.find((agent) => toHexFromId(agent.id) === loadedAgentId.current) !== undefined
      );

      return toHexFromId(exists?.id) as M_Room['id'];
    }
  }) ?? {};

  return {
    roomId,
    loading: isLoading || isPending,
    debug: { loadedAgentId: loadedAgentId.current, roomId, loaded }
  };
};

export const useLastRoomIdPref = (): [
  T_Maybe<Room['id']>,
  (v: T_Maybe<Room['id']>) => void,
  boolean
] => {
  const [roomID, setRoomID] = useState<T_Maybe<Room['id']>>(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const lastRoomId = await persistentLayer.get('lastRoomId');

      if (lastRoomId !== null) {
        //console.log('###$ROOM', { lastRoomId });
        setRoomID(lastRoomId as Room['id']);
      }

      setLoaded(true);
    })();
  }, []);

  return [
    roomID,
    (v) => {
      if (v !== undefined) {
        void persistentLayer.set('lastRoomId', v);
      } else {
        void persistentLayer.remove('lastRoomId');
      }
      setRoomID(v);
    },
    loaded
  ];
};

export const useLastAccountIdPref = (): [
  T_Maybe<string>,
  (v: T_Maybe<string>) => void,
  boolean
] => {
  const [accountID, setAccountID] = useState<T_Maybe<string>>(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const lastAccountId = await persistentLayer.get('lastAccountId');

      if (lastAccountId !== null) {
        setAccountID(lastAccountId as string);
      }
      setLoaded(true);
    })();
  }, []);

  return [
    accountID,
    (v) => {
      if (v !== undefined) {
        persistentLayer.set('lastAccountId', v);
      }
      setAccountID(v);
    },
    loaded
  ];
};

/**
 * Hook to track whether the user has seen the agent setup page.
 *
 * @returns Tuple of [hasSeenSetup, setHasSeenSetup, loaded]
 */
export const useHasSeenAgentSetup = (): [boolean, (v: boolean) => void, boolean] => {
  const [hasSeenSetup, setHasSeenSetup] = useState<boolean>(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const value = await persistentLayer.get('hasSeenAgentSetup');

      if (value !== null) {
        setHasSeenSetup(value === 'true');
      }
      setLoaded(true);
    })();
  }, []);

  return [
    hasSeenSetup,
    (v) => {
      persistentLayer.set('hasSeenAgentSetup', v.toString());
      setHasSeenSetup(v);
    },
    loaded
  ];
};
